import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import tinycolor, { Instance as TinyColor } from 'tinycolor2';
import useStyles from './styles'
import { Box, Button, Divider, Stack } from "@mui/material";
import HueSlider from "../hueSlider/hueSlider";

export type CustomColorSelectorProps = {
    value: TinyColor,
    onColorSelect: (color: TinyColor) => void,
    disableSwatches?: boolean
    onAddSwatch?: (color: TinyColor) => void,
    onClose: () => void
}

const CustomColorSelector: React.FC<CustomColorSelectorProps> = ({value, disableSwatches, onColorSelect, onAddSwatch, onClose}) => {
    const spaceContainer = useRef<HTMLDivElement>(null)
    const pointerRef = useRef<HTMLDivElement>(null)
    const containerBounds = useRef<DOMRect>()
    const originalValue = useRef(value.toHsv())
    const rafRef = useRef<number>()
    const [active, setActive] = useState(false)
    const hsv = value.toHsv();
    const [currentHue, setCurrentHue] = useState(hsv.h)
    const currentHueRef = useRef(hsv.h)
    const onColorSelectRef = useRef(onColorSelect)
    useEffect(() => { onColorSelectRef.current = onColorSelect }, [onColorSelect])

    const baseColor = useMemo(() => tinycolor({
        h: currentHue,
        s: 1,
        v: 1
    }), [currentHue])
    const {classes} = useStyles({color: baseColor, thumbnailColor: value})

    const [hexInput, setHexInput] = useState(value.toHex8String())
    const hexInputFocused = useRef(false)

    useEffect(() => {
        if (!hexInputFocused.current) {
            setHexInput(value.toHex8String())
        }
    }, [value])

    const handleHexChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value
        setHexInput(raw)
        const parsed = tinycolor(raw)
        if (parsed.isValid()) {
            onColorSelect(parsed)
        }
    }, [onColorSelect])

    const handleHexFocus = useCallback(() => {
        hexInputFocused.current = true
    }, [])

    const handleHexBlur = useCallback(() => {
        hexInputFocused.current = false
        const parsed = tinycolor(hexInput)
        if (!parsed.isValid()) {
            setHexInput(value.toHex8String())
        }
    }, [hexInput, value])

    const selectedColorRef = useRef(value)

    const applyPosition = useCallback((x: number, y: number) => {
        if (pointerRef.current) {
            pointerRef.current.style.left = `${x * 100}%`
            pointerRef.current.style.top = `${y * 100}%`
        }
        const color = tinycolor({
            h: currentHueRef.current,
            s: x,
            v: 1 - y,
            a: originalValue.current.a
        })
        selectedColorRef.current = color
        onColorSelectRef.current(color)
    }, [])

    const onHueChange = useCallback((hue: number) => {
        currentHueRef.current = hue
        setCurrentHue(hue)
    }, [])

    const onMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (!spaceContainer.current) return;
        const bounds = spaceContainer.current.getBoundingClientRect()
        containerBounds.current = bounds
        setActive(true)
        const { left, right, top, bottom } = bounds;
        const width = right - left;
        const height = bottom - top;
        const deltaX = event.clientX - left;
        const deltaY = event.clientY - top;
        applyPosition(
            (deltaX < 0 ? 0 : Math.min(deltaX, width)) / width,
            (deltaY < 0 ? 0 : Math.min(deltaY, height)) / height
        )
    }, [applyPosition])

    const onMouseMove = useCallback((event: MouseEvent) => {
        if (!containerBounds.current) return;
        const { clientX, clientY } = event;
        cancelAnimationFrame(rafRef.current!)
        rafRef.current = requestAnimationFrame(() => {
            if (!containerBounds.current) return;
            const { left, right, top, bottom } = containerBounds.current;
            const width = right - left;
            const height = bottom - top;
            const deltaX = clientX - left;
            const deltaY = clientY - top;
            applyPosition(
                (deltaX < 0 ? 0 : Math.min(deltaX, width)) / width,
                (deltaY < 0 ? 0 : Math.min(deltaY, height)) / height
            )
        })
    }, [applyPosition])

    const onMouseUp = useCallback(() => {
        setActive(false)
        containerBounds.current = undefined
    }, [])

    useEffect(() => {
        if (active) {
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp)
        } else {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp)
        }
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp)
        }
    }, [active])

    const onCancelClick = useCallback(() => {
        onColorSelect(tinycolor(originalValue.current))
        onClose();
    }, [onColorSelect, onClose]);

    const onAddClick = useCallback(() => {
        onAddSwatch && onAddSwatch(selectedColorRef.current)
    }, [onAddSwatch]);

    return <Box>
        <Stack direction="row" gap={1} sx={{mb: 0.75}}>
            <div className={classes.thumbnail}>
                <div className={classes.thumbnailInner} />
            </div>
            <input
                className={classes.colorDisplay}
                value={hexInput}
                onChange={handleHexChange}
                onFocus={handleHexFocus}
                onBlur={handleHexBlur}
                spellCheck={false}
                maxLength={9}
            />
        </Stack>
        <Divider className={classes.divider} />
        <Stack direction="row" gap={0.75}>
            <div className={classes.colorSpace} ref={spaceContainer} onMouseDown={onMouseDown}>
                <div className={classes.colorPointer} ref={pointerRef} style={{top: `${(1 - hsv.v)*100}%`, left: `${hsv.s*100}%`}}></div>
            </div>
            <div className={classes.hueSliderContainer}>
                <HueSlider hue={currentHue} onHueChange={onHueChange} />
            </div>
        </Stack>
        <Stack direction="row" sx={{marginTop: 1}} justifyContent="space-between" alignItems="center">
            <Button variant="outlined" size="small"  sx={{minWidth: '1px', padding: '5px', lineHeight: '1'}} onClick={onCancelClick}>Cancel</Button>
            <Button variant="contained" size="small" sx={{minWidth: '1px', paddingY: '5px', lineHeight: '1'}} onClick={onAddClick}>Add</Button>
        </Stack>
    </Box>;
}


export default memo(CustomColorSelector)